"use strict";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Badge, Box, Button, Grid, Group, Input, Menu } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDownload,
  IconSearch,
  IconTimeDuration15,
} from "@tabler/icons-react";
import { ColDef, GridReadyEvent, ModuleRegistry } from "ag-grid-community";
import {
  IServerSideDatasource,
  IServerSideGetRowsRequest,
  ServerSideRowModelModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import {
  paginationPageSize,
  paginationPageSizeSelector,
  repositoryPos,
} from "../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../_base/component/_layout/_breadcrumb";
import { formatDateTransfer } from "../../common/FormatDate/FormatDate";
import LoadingOverlayCustom from "../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { ComboboxItem } from "../../model/ComboboxItem";
import { MessageResponse } from "../../model/MessageResponse";

ModuleRegistry.registerModules([ServerSideRowModelModule]);
export interface DocumentInventoryFile {
  id: string;
  empId: number;
  linkDoc: string | null;
  usedTimeMs: number | null;
  createDate: string;
  filename?: string | null;
  attribute1?: string | null;
  attribute2?: string | null;
  attribute3?: string | null;
  attribute4?: string | null;
  attribute5?: string | null;
}
const ListWarehouseData = () => {
  const [loading, setLoading] = useState(true);
  const gridRef = useRef<AgGridReact<any>>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const datasourceRef = useRef<IServerSideDatasource | null>(null);
  const [height, setHeight] = useState(800);
  const [dataEmployee, setDataEmployee] = useState<ComboboxItem[]>([]);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(30000);

  const [visible, { toggle, close, open }] = useDisclosure(false);
  const [isListEmpty, setIsListEmpty] = useState(false);
  // Xoá useState search, chỉ dùng form
  const containerStyle = useMemo(
    () => ({ width: "100%", height: height }),
    [height],
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

  const sizeGrid = { base: 12, sm: 6, md: 6, lg: 2.2 };

  useEffect(() => {
    let intervalId: any;
    if (autoRefreshInterval > 0) {
      intervalId = setInterval(() => {
        gridRef.current?.api?.refreshServerSide?.({ purge: true });
      }, autoRefreshInterval);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefreshInterval]);

  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      // 400 là tổng khoảng cách cần trừ (breadcrumb, filters, padding, pagination)
      const newHeight = window.innerHeight - (210 + headerHeight);
      setHeight(newHeight);
    };

    // Đợi DOM render xong rồi mới tính
    setTimeout(updateHeight, 0);
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  // Chỗ này để làm lọc dùng form cho đỡ lag so với state
  const form = useForm<{
    KeySearch: string;
    EmpId: number | null;
    FormDate: string | null;
    ToDate: string | null;
  }>({
    initialValues: {
      KeySearch: "",
      EmpId: null,
      FormDate: null,
      ToDate: null,
    },
  });
  const fetchData = async (request: IServerSideGetRowsRequest) => {
    try {
      const currentPageSize =
        gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
      const skip = request.startRow || 0;
      const take = currentPageSize;
      let url = `/api/v1/DocumentInventory/get-list?Skip=${skip}&Take=${take}`;
      const values = form.getValues();
      // Thêm các filter vào url
      if (values.KeySearch) {
        url += `&KeySearch=${encodeURIComponent(values.KeySearch)}`;
      }
      if (values.EmpId) {
        url += `&EmpId=${values.EmpId}`;
      }
      if (values.FormDate) {
        url += `&FormDate=${values.FormDate}`;
      }
      if (values.ToDate) {
        url += `&ToDate=${values.ToDate}`;
      }
      return await repositoryPos.get<MessageResponse<DocumentInventoryFile[]>>(
        url,
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Call api bên server
  const getServerSideDatasource = useCallback((): IServerSideDatasource => {
    return {
      getRows: async (params) => {
        var response = await fetchData(params.request);
        // adding delay to simulate real server call
        if (response && response.success) {
          // Tách row TOTAL ra khỏi data chính
          const allData = response.data || [];
          const total = (response as any)?.totalCount ?? allData.length;
          params.success({ rowData: allData, rowCount: total });
          setIsListEmpty(total === 0);
          setLoading(false);
        } else {
          // inform the grid request failed or empty
          params.success({ rowData: [], rowCount: 0 });
          setIsListEmpty(true);
          setLoading(false);
        }
      },
    };
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // đăng datasource đúng để ag-Grid gọi getRows
      const datasource = getServerSideDatasource();
      datasourceRef.current = datasource;
      params.api.setGridOption("serverSideDatasource", datasource);
      console.log("Grid ready, datasource registered");
    },
    [getServerSideDatasource],
  );

  const getRowId = useCallback((params: any) => {
    const d = params.data || {};
    const id =
      `${d.createDate ?? ""}_${d.filename ?? ""}_${d.attribute1 ?? ""}`.replace(
        /(^_+|_+$)/g,
        "",
      );
    // debug: console.log('[GetTHEKPHG] rowId=', id, params.data);
    return id || "";
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#e7f5ff",
        fontWeight: 700,
        fontSize: "14px",
        borderTop: "3px solid #15aabf",
      };
    }
    return undefined;
  }, []);

  // Hàm sửa lý sự kiện sắp xếp
  const handleSortChanged = useCallback(() => {
    const columnState = gridRef.current?.api?.getColumnState() || [];
    // tìm cột có sort (asc/desc)
    const sortedCol = columnState.find((c: any) => c.sort);
    if (sortedCol) {
      form.setFieldValue("SortBy", sortedCol.colId);
      form.setFieldValue(
        "SortDirection",
        (sortedCol.sort || "").toUpperCase() === "ASC" ? "ASC" : "DESC",
      );
    } else {
      form.setFieldValue("SortBy", null);
      form.setFieldValue("SortDirection", null);
    }
    // reload server-side data với sort mới
    gridRef.current?.api?.refreshServerSide?.({ purge: true });
  }, [form]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      // { field: "empId", headerName: "Id nhân viên", size: 120 },
      // {
      //     field: "tenNv", headerName: "Tên nhân viên", width: 300,
      //     valueGetter: (params: any) => {
      //         const empId = params.data?.empId;
      //         const emp = dataEmployee.find(item => Number(item.value) === Number(empId));
      //         return emp ? emp.label : "";
      //     },
      // },
      {
        field: "action",
        headerName: "Thao tác",
        width: 110,
        pinned: "left",
        cellRenderer: (params: any) => {
          const linkDoc = params.data?.linkDoc;
          const status = params.data?.attribute1 === "SUCCESS";
          if (linkDoc) {
            const style: React.CSSProperties = {
              pointerEvents: status ? "auto" : "none",
              opacity: status ? 1 : 0.5,
              cursor: status ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
            };
            return (
              <a
                href={status ? linkDoc : undefined}
                target={status ? "_blank" : undefined}
                rel={status ? "noopener noreferrer" : undefined}
                title={status ? "Nhấn để tải" : "Không thể tải"}
                aria-disabled={!status}
                style={style}
              >
                <IconDownload size={20} color="#15aabf" />
              </a>
            );
          }
          return "";
        },
      },
      {
        field: "filename",
        headerName: "Tên file",
        width: 350,
      },
      {
        field: "attribute1",
        headerName: "Trạng thái",
        width: 200,
        cellRenderer: (params: any) => {
          const status = params.value;
          let color = "blue";
          if (status === "SUCCESS") color = "green";
          if (status === "FAIL") color = "red";
          return (
            <Badge color={color} variant="light">
              {status}
            </Badge>
          );
        },
      },
      { field: "usedTimeMs", headerName: "Thời gian xử lý (ms)", width: 200 },
      { field: "attribute3", headerName: "Tổng số dòng", width: 180 },
      { field: "attribute4", headerName: "Số dòng đã xử lý", width: 180 },
      {
        headerName: "% Hoàn thành",
        width: 180,
        valueGetter: (params: any) => {
          const status = params.data?.attribute1;
          if (status === "SUCCESS") return "100.00%";
          const total = Number(params.data?.attribute3) || 0;
          const processed = Number(params.data?.attribute4) || 0;
          if (total === 0) return "0%";
          const percent = (processed / total) * 100;
          return `${percent.toFixed(2)}%`;
        },
      },
      {
        field: "createDate",
        headerName: "Ngày tạo",
        width: 200,
        cellRenderer: (params: any) => {
          return formatDateTransfer(params.data?.createDate);
        },
      },
    ],
    [form.getValues()],
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      sortable: true,
      resizable: true,
    };
  }, []);
  const handleSearch = useCallback(() => {
    // Xóa lỗi cũ
    form.setErrors({});

    const values = form.getValues();

    // Validate FormDate <= ToDate
    if (
      values.FormDate &&
      values.ToDate &&
      dayjs(values.FormDate).isAfter(dayjs(values.ToDate))
    ) {
      form.setFieldError(
        "FormDate",
        "Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc",
      );
      return;
    }

    setLoading(true);
    const api = gridRef.current?.api;
    if (!api) {
      console.log("API not ready");
      return;
    }

    // Refresh server-side cache để gọi lại getRows với filter mới
    (api as any).refreshServerSide({ purge: true });
  }, [form]);

  // Lấy danh sách nhân viên
  // const getDataEmpSelect = async () => {
  //     const res = await getTblDmEmployeeSelect();
  //     setDataEmployee(
  //         res
  //             ?.filter(
  //                 (item) =>
  //                     item.value != null && item.text != null && item.value !== "-1"
  //             )
  //             ?.map((item) => ({
  //                 value: item.value,
  //                 label: `${item.att1} - ${item.text} `,
  //             }))
  //     );
  // };
  // useEffect(() => {
  //     getDataEmpSelect();
  // }, []);
  return (
    <Box pos={"relative"} style={containerStyle}>
      <LoadingOverlayCustom visible={visible} />
      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "10px",
          overflowX: "hidden",
        }}
      >
        <Group justify="space-between" gap={0}>
          <BreadcrumbLayout1 />
        </Group>
        <Grid mt={10}>
          <Grid.Col span={sizeGrid}>
            <Input
              placeholder="Nhập tìm kiếm"
              type="text"
              leftSection={<IconSearch size={20} color="#15aabf" />}
              value={form.getValues().KeySearch}
              onChange={(e) => form.setFieldValue("KeySearch", e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              key={form.key("FormDate")}
              {...form.getInputProps("FormDate")}
              value={
                form.getValues().FormDate
                  ? dayjs(form.getValues().FormDate, "YYYY-MM-DD").toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "FormDate",
                  date ? dayjs(date).format("YYYY-MM-DD") : null,
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              key={form.key("ToDate")}
              {...form.getInputProps("ToDate")}
              value={
                form.getValues().ToDate
                  ? dayjs(form.getValues().ToDate, "YYYY-MM-DD").toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "ToDate",
                  date ? dayjs(date).format("YYYY-MM-DD") : null,
                )
              }
              clearable
            />
          </Grid.Col>
          {/* <Grid.Col span={sizeGrid}>
                        <Select
                            key={dataEmployee.length}
                            placeholder={"Chọn nhân viên"}
                            searchable
                            clearable
                            leftSection={<IconUserCode color="#15aabf" />}
                            comboboxProps={{
                                transitionProps: { transition: "pop", duration: 200 },
                            }}
                            data={dataEmployee
                                .map((ele) => ({
                                    label: ele.label,
                                    value: ele.value,
                                }))}
                            nothingFoundMessage={"Loading..."}
                            {...form.getInputProps("EmpId")}
                            onClick={() => {
                                if (dataEmployee.length === 0) {
                                    getDataEmpSelect();
                                }
                            }}
                        />
                    </Grid.Col> */}
          <Grid.Col span={sizeGrid}>
            <Group gap={5}>
              <Button color="blue" variant="outline" onClick={handleSearch}>
                Tìm kiếm
              </Button>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button variant="outline" color="gray" px={10}>
                    <span style={{ marginRight: 5 }}>Tự động làm mới:</span>
                    <span style={{ fontWeight: 600 }}>
                      {autoRefreshInterval === 0
                        ? "Off"
                        : autoRefreshInterval === 30000
                          ? "30s"
                          : autoRefreshInterval === 60000
                            ? "1m"
                            : autoRefreshInterval === 300000
                              ? "5m"
                              : "15m"}
                    </span>
                  </Button>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>Tự động tải lại</Menu.Label>
                  <Menu.Item
                    leftSection={
                      autoRefreshInterval === 0 && (
                        <span style={{ fontSize: 18 }}>•</span>
                      )
                    }
                    onClick={() => setAutoRefreshInterval(0)}
                  >
                    Tắt
                  </Menu.Item>
                  <Menu.Item
                    leftSection={
                      autoRefreshInterval === 30000 && (
                        <span style={{ fontSize: 18 }}>•</span>
                      )
                    }
                    onClick={() => setAutoRefreshInterval(30000)}
                  >
                    Mỗi 30s
                  </Menu.Item>
                  <Menu.Item
                    leftSection={
                      autoRefreshInterval === 60000 && (
                        <span style={{ fontSize: 18 }}>•</span>
                      )
                    }
                    onClick={() => setAutoRefreshInterval(60000)}
                  >
                    Mỗi 1 phút
                  </Menu.Item>
                  <Menu.Item
                    leftSection={
                      autoRefreshInterval === 300000 && (
                        <span style={{ fontSize: 18 }}>•</span>
                      )
                    }
                    onClick={() => setAutoRefreshInterval(300000)}
                  >
                    Mỗi 5 phút
                  </Menu.Item>
                  <Menu.Item
                    leftSection={
                      autoRefreshInterval === 900000 && (
                        <span style={{ fontSize: 18 }}>•</span>
                      )
                    }
                    onClick={() => setAutoRefreshInterval(900000)}
                  >
                    Mỗi 15 phút
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Grid.Col>
        </Grid>
      </div>
      {isListEmpty && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            backgroundColor: "#fff",
            border: "1px solid #dee2e6",
            borderTop: "none",
          }}
        >
          <span>Bạn chưa có export báo cáo nào</span>
        </div>
      )}
      <div
        style={{ ...gridStyle, display: isListEmpty ? "none" : "block" }}
        className={"ag-theme-quartz"}
      >
        <AgGridReact<any>
          ref={gridRef}
          loading={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType={"serverSide"}
          pagination={true}
          paginationPageSize={100}
          paginationPageSizeSelector={paginationPageSizeSelector}
          onSortChanged={handleSortChanged}
          onGridReady={onGridReady}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          localeText={AG_GRID_LOCALE_VN}
          overlayNoRowsTemplate={
            '<span style="padding: 10px;">Bạn chưa có export báo cáo nào</span>'
          }
        />
      </div>
    </Box>
  );
};
export default ListWarehouseData;
